import { joinLottery, lotteryPushNotify } from '@/api/vip';
import { getTokenAndLocation } from '@/utils';
import { getSystemCode, getVersion } from '@/utils/systemCode';
import Toast from '@/components/toast';
import { Storage } from '@/utils/storage';
import dayjs from 'dayjs';
import {
  hasCalendar,
  setCalendarSchedule,
  // openSystemSetting,
} from '@/utils/calender';
import { env } from '@hb/hbridge';
import { LOTTERY_SUBSCRIBED } from '@/const/vip';


/**
 * 抽奖工具对象
 */
// eslint-disable-next-line import/prefer-default-export
export const LotteryUtils = {
  /**
   * 参与抽奖
   * @param {Object} item - 抽奖项目信息
   * @param {Object} lotteryData - 抽奖活动数据
   * @param {Function} onSuccess - 成功回调函数
   * @param {Function} onError - 错误回调函数
   * @returns {Promise} 返回抽奖结果
   */
  async joinLotteryDraw(item = {}, onSuccess, onError) {
    try {
      const { cityCode, adCode, token } = await getTokenAndLocation();
      const {
        batchNo, campaignCode, activityCode = '', lotteryExchangeProductIdList: lotteryExchangeProductId,
      } = item;

      const param = {
        adCode,
        cityCode,
        token,
        campaignCode,
        lotteryExchangeProductId,
        activityCode,
        appCode: 'platform',
        batchNo: batchNo || '',
      };

      const res = await joinLottery(param);

      if (res && res.data && Object.keys(res.data).length) {
        // 成功回调
        if (typeof onSuccess === 'function') {
          onSuccess(res.data);
        }
        return res.data;
      }
      const errorMsg = '参与失败';
      Toast.show(errorMsg);
      if (typeof onError === 'function') {
        onError(errorMsg);
      }
      return null;
    } catch (error) {
      const errorMsg = '参与失败';
      Toast.show(errorMsg);
      if (typeof onError === 'function') {
        onError(errorMsg, error);
      }
      return null;
    }
  },

  /**
   * 设置抽奖提醒
   * @param {Object} lotteryData - 抽奖活动数据
   * @param {Boolean} isNewVersion - 是否新版本
   * @param {Boolean} haveSubscribed - 是否已订阅
   * @param {Function} onSuccess - 成功回调
   * @param {Function} onError - 错误回调
   */
  async setRemind(
    lotteryData = {},
    isNewVersion = false,
    haveSubscribed = false,
    onSuccess,
    onError,
  ) {
    try {
      const version = await getVersion();

      // push通知
      if (isNewVersion && env.isEasyBikeApp) {
        const {
          cityCode, adCode, token, userNewId,
        } = await getTokenAndLocation();
        const { campaignCode, lotteryTime = '20:00' } = lotteryData;
        const param = {
          adCode,
          cityCode,
          token,
          userNewId,
          version,
          campaignCode,
          systemCode: getSystemCode(),
          appCode: 'platform',
          notifyTime: lotteryTime,
          batchNo: lotteryData.batchNo || '',
        };
        lotteryPushNotify(param);
      }

      // 日历提醒
      if (!env.isEasyBikeApp || version < '6.46.0' || haveSubscribed) return;

      const res = await hasCalendar({});
      if (res.hasPermission) {
        await this.writeNoticeOnce(lotteryData, onSuccess, onError);
        return;
      }

      // ios直接尝试写入，调用日历api，以唤起日历权限授权
      if (env.isIOS) {
        await this.writeNoticeOnce(lotteryData, onSuccess, onError);
      }
      // else if (env.isAndroid) {
      //   // Toast.show('请在设置中开启日历权限!');
      //   // setTimeout(() => {
      //   //   openSystemSetting();
      //   // }, 1000);
      // }
    } catch (error) {
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  },

  /**
   * 检查是否已订阅提醒
   * @param {Function} onSuccess - 成功回调，参数为是否已订阅
   * @param {Function} onError - 错误回调
   * @returns {Promise<Boolean>} 返回是否已订阅
   */
  async checkRemind(onSuccess, onError) {
    try {
      if (!env.isEasyBikeApp) {
        if (typeof onSuccess === 'function') {
          onSuccess(false);
        }
        return false;
      }

      const version = await getVersion();
      if (version < '6.46.0') {
        if (typeof onSuccess === 'function') {
          onSuccess(false);
        }
        return false;
      }

      // 检查缓存中是否有订阅记录
      const res = await Storage.getItem(LOTTERY_SUBSCRIBED);
      const hasSubscribed = !!res;

      if (typeof onSuccess === 'function') {
        onSuccess(hasSubscribed);
      }

      return hasSubscribed;
    } catch (error) {
      if (typeof onError === 'function') {
        onError(error);
      }
      return false;
    }
  },

  /**
   * 获取指定日期的开始和结束时间
   * @param {Number} dayOffset - 天数偏移量
   * @returns {Object} 时间配置对象
   */
  getScheduleTime(dayOffset = 0) {
    const date = new Date();
    date.setDate(date.getDate() + dayOffset);
    return {
      startTime: Math.floor(new Date(date).setHours(20, 10, 0) / 1000),
      endTime: Math.floor(new Date(date).setHours(20, 20, 0) / 1000),
      title: '哈啰会员日日抽开奖啦，今日必抽，大奖等你拿！',
      notes: 'hellobike://hellobike.com/openWeb?webUrl=https%3A%2F%2Fm.hellobike.com%2FAppPlatformH5%2Flatest%2Findex.html%23%2Fvip%2FnewTransfer%3Fadsource%3Dchoujiang',
    };
  },

  /**
   * 只写入一次日历
   * 当前开奖时间之前，提醒当天
   * 当天开奖时间之后，提醒第二天
   * @param {Object} lotteryData - 抽奖活动数据
   * @param {Function} onSuccess - 成功回调
   * @param {Function} onError - 错误回调
   */
  async writeNoticeOnce(lotteryData = {}, onSuccess, onError) {
    try {
      const { lotteryTime } = lotteryData;
      const todayStart = dayjs().startOf('day');
      const todayEnd = dayjs().endOf('day');
      // 当天开奖时间 ： 当天起始时间 + lotteryTime（20:00、21：00）
      const todayLotteryTime = dayjs(todayStart).add(lotteryTime.split(':')[0], 'hour');
      // 第二天开奖时间
      const nextLotteryTime = dayjs(todayLotteryTime).add(1, 'day');
      let res;
      // 是否第二天提醒
      let isRemindDayTomorrow = 0;

      // 当前时间 < 开奖时间 -> 提醒当天
      if (dayjs().isBefore(dayjs(todayLotteryTime))) {
        res = await setCalendarSchedule({ ...this.getScheduleTime(0) });
        isRemindDayTomorrow = 0;
      } else if (dayjs().isAfter(dayjs(todayLotteryTime)) && dayjs().isBefore(todayEnd)) {
        // 开奖时间 > 当天时间 > 当天结束时间 -> 提醒第二天
        res = await setCalendarSchedule({ ...this.getScheduleTime(1) });
        isRemindDayTomorrow = 1;
      }

      // 提醒设置缓存 & 过期时间
      if (res.isSuccess) {
        const expireTime = isRemindDayTomorrow
          ? new Date(nextLotteryTime) : new Date(todayLotteryTime);
        Storage.setItem(LOTTERY_SUBSCRIBED, true, expireTime);

        if (typeof onSuccess === 'function') {
          onSuccess(true);
        }
        Toast.show('添加日历提醒成功，将在开抢前10分钟提醒您');
      }
    } catch (error) {
      Toast.show('添加日历提醒失败');
      console.log('添加日历失败的原因', error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  },

  /**
   * 写入7天日历提醒
   * @param {Function} onSuccess - 成功回调
   * @param {Function} onError - 错误回调
   */
  async writeNotice(onSuccess, onError) {
    try {
      let firstDaySuccess = false;

      // 统一处理7天的提醒
      for (let i = 0; i < 7; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await setCalendarSchedule({ ...this.getScheduleTime(i) });
        // 如果是第一天且成功，设置标志
        if (res.isSuccess) {
          const dateRange = this.getScheduleTime(i);
          const key = `${dateRange.startTime}${dateRange.endTime}${dateRange.title}`;
          Storage.setItem(key, true);
          if (i === 0) {
            firstDaySuccess = true;
          }
        }
      }

      // 如果第一天成功，显示提示
      if (firstDaySuccess) {
        if (typeof onSuccess === 'function') {
          onSuccess(true);
        }
        Toast.show('添加日历提醒成功，将在开抢前10分钟提醒您');
      }
    } catch (error) {
      Toast.show('添加日历提醒失败');
      console.log('添加日历失败的原因', error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  },

};
